// core/services/firebase/firestore.ts - Helpers Firestore Multi-Tenant

import { 
  collection, 
  doc, 
  CollectionReference, 
  DocumentReference,
  getDoc,
  getDocs,
  setDoc,
  updateDoc,
  deleteDoc,
  query,
  where,
  WhereFilterOp
} from 'firebase/firestore';
import { db } from './config';
import { extractTenantFromHostname } from '../../utils/domainHelper';

/**
 * Identifica o tenant atual baseado no hostname
 */
/**
 * Identifica o tenant atual baseado no hostname
 */
export const getCurrentTenantId = (): string => {
  const hostname = window.location.hostname;
  
  // Desenvolvimento local
  if (import.meta.env.DEV) {
    const devTenant = localStorage.getItem('dev_tenant_id');
    if (devTenant) return devTenant;
    
    // Se estiver no localhost, podemos estar simulando um domínio
    if (hostname === 'localhost' || hostname === '127.0.0.1') {
       return 'platform';
    }
  }
  
  const tenantId = extractTenantFromHostname(hostname);
  
  if (tenantId === null) return 'platform';
  if (tenantId === 'custom-domain') {
    // Buscar tenant por domínio (usa cache da sessão)
    const cached = sessionStorage.getItem(`tenant_${hostname}`);
    if (cached) return cached;
    
    // Fallback para default enquanto o component useTenant busca no Firestore
    return 'default';
  }
  
  return tenantId;
};

/**
 * Retorna referência para uma coleção específica de um tenant
 */
export const tenantCollection = <T = any>(
  collectionName: string, 
  tenantId?: string
): CollectionReference<T> => {
  const tenant = tenantId || getCurrentTenantId();
  return collection(db, `tenants/${tenant}/${collectionName}`) as CollectionReference<T>;
};

/**
 * Retorna referência para um documento específico de um tenant
 */
export const tenantDoc = <T = any>(
  collectionName: string, 
  docId: string, 
  tenantId?: string
): DocumentReference<T> => {
  const tenant = tenantId || getCurrentTenantId();
  return doc(db, `tenants/${tenant}/${collectionName}/${docId}`) as DocumentReference<T>;
};

/**
 * Retorna referência para a coleção global de tenants
 */
export const tenantsCollection = () => {
  return collection(db, 'tenants');
};

/**
 * Retorna referência para um documento de tenant específico
 */
export const tenantConfigDoc = (tenantId: string) => {
  return doc(db, `tenants/${tenantId}`);
};

/**
 * Retorna referência para o registro de mapeamento de domínios
 */
export const tenantRegistryCollection = () => {
  return collection(db, 'tenant-registry');
};

/**
 * Helper para definir tenant em desenvolvimento
 */
export const setDevTenant = (tenantId: string) => {
  localStorage.setItem('dev_tenant_id', tenantId);
  console.log(`✅ Development tenant set to: ${tenantId}`);
};

/**
 * Helper para limpar cache de tenant
 */
export const clearTenantCache = () => {
  const hostname = window.location.hostname;
  sessionStorage.removeItem(`tenant_${hostname}`);
  console.log('✅ Tenant cache cleared');
};

// Serviço de Tenants
export const tenantService = {
  /**
   * Cria um novo tenant
   */
  async createTenant(tenantData: any) {
    const tenantRef = doc(db, 'tenants', tenantData.id);
    await setDoc(tenantRef, {
      ...tenantData,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    
    // Criar coleções iniciais
    await Promise.all([
      setDoc(doc(db, `tenants/${tenantData.id}/config/site`), {
        pages: [],
        navigation: [],
        settings: tenantData.settings,
      }),
      setDoc(doc(db, `tenants/${tenantData.id}/config/branding`), tenantData.branding),
    ]);
    
    // Registrar mapeamento de domínio
    await setDoc(doc(db, 'tenant-registry', tenantData.id), {
      tenantId: tenantData.id,
      domains: [
        tenantData.domain,
        `www.${tenantData.domain}`,
        tenantData.subdomainStaging
      ],
      primaryDomain: tenantData.domain,
      verified: false,
      createdAt: new Date().toISOString(),
      updatedAt: new Date().toISOString(),
    });
    
    return tenantData.id;
  },
  
  /**
   * Busca tenant por ID
   */
  async getTenant(tenantId: string) {
    const tenantRef = doc(db, 'tenants', tenantId);
    const snapshot = await getDoc(tenantRef);
    return snapshot.exists() ? snapshot.data() : null;
  },
  
  /**
   * Busca tenant por domínio
   */
  async getTenantByDomain(domain: string) {
    const registryRef = collection(db, 'tenant-registry');
    const q = query(registryRef, where('domains', 'array-contains', domain));
    const snapshot = await getDocs(q);
    
    if (!snapshot.empty) {
      const registryData = snapshot.docs[0].data();
      return await this.getTenant(registryData.tenantId);
    }
    
    return null;
  },
  
  /**
   * Atualiza tenant
   */
  async updateTenant(tenantId: string, updates: any) {
    const tenantRef = doc(db, 'tenants', tenantId);
    await updateDoc(tenantRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  },
  
  async deleteTenant(tenantId: string) {
    const tenantRef = doc(db, 'tenants', tenantId);
    await deleteDoc(tenantRef);
    // TODO: Deletar todas as sub-coleções
    // Isso requer Cloud Functions em produção
  },

  /**
   * Busca o registro de domínio de um tenant
   */
  async getTenantRegistry(tenantId: string) {
    const registryRef = doc(db, 'tenant-registry', tenantId);
    const snapshot = await getDoc(registryRef);
    return snapshot.exists() ? snapshot.data() : null;
  },

  /**
   * Atualiza o registro de domínios
   */
  async updateTenantRegistry(tenantId: string, updates: any) {
    const registryRef = doc(db, 'tenant-registry', tenantId);
    await updateDoc(registryRef, {
      ...updates,
      updatedAt: new Date().toISOString(),
    });
  },

  /**
   * MOCK: Verifica status do DNS
   */
  async checkDnsStatus(tenantId: string, _domain: string) {
    // Em produção, isso chamaria uma Cloud Function que faz lookup DNS
    // Por enquanto, simulamos uma verificação
    console.log(`Checking DNS for tenant ${tenantId}...`);
    return new Promise(resolve => {
      setTimeout(() => {
        resolve({
          verified: Math.random() > 0.5,
          checkedAt: new Date().toISOString()
        });
      }, 1500);
    });
  }
};
